'use client';

import { useMemo } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from '../../config/dayjs';

import { useUserActivityHistory } from './hooks/useUserActivityHistory';
import UserActivityFilters from './components/UserActivityFilters';
import UserActivityDetailDialog from './components/UserActivityDetailDialog';

export default function UserActivityHistoryPage() {
  const {
    activeTab,
    setActiveTab,
    selectedDate,
    setSelectedDate,
    selectedCompanies,
    setSelectedCompanies,
    selectedUsers,
    setSelectedUsers,
    loading,
    data,
    selectedDetailUser,
    searchDetail,
    setSearchDetail,
    filteredEndpointHistory,
    fetchActivityHistory,
    openDetail,
    closeDetail,
    warningMessage
  } = useUserActivityHistory();

  const layoutFormatDate = 'DD MMM YYYY HH:mm';

  const columns = useMemo(
    () => [
      {
        field: 'username',
        headerName: 'Username',
        minWidth: 150,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.username || r.user_name || r.name || value || '-';
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.username || r.user_name || r.name || params.value || '-';
          return (
            <Typography variant="body2" fontWeight={600} color="primary.main" noWrap>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'ip',
        headerName: 'IP Address',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.ip || r.ip_address || r.ipAddress || value || '-';
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.ip || r.ip_address || r.ipAddress || params.value || '-';
          return (
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13, color: '#334155' }} noWrap>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'company',
        headerName: 'Company',
        minWidth: 150,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.company || r.company_name || r.companyName || value || '-';
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.company || r.company_name || r.companyName || params.value || '-';
          return (
            <Typography variant="body2" color="text.secondary" noWrap title={val}>
              {val}
            </Typography>
          );
        }
      },
      ...(activeTab === 'pod'
        ? [
            {
              field: 'podId',
              headerName: 'Pod ID',
              width: 105,
              align: 'center',
              headerAlign: 'center',
              valueGetter: (value, row) => {
                const r = row || {};
                return r.podId ?? r.pod_id ?? r.pod ?? value ?? '-';
              },
              renderCell: (params) => {
                const r = params.row || {};
                const val = r.podId ?? r.pod_id ?? r.pod ?? params.value;
                if (val == null || val === '-') {
                  return (
                    <Typography variant="body2" color="text.disabled">
                      -
                    </Typography>
                  );
                }
                return (
                  <Chip
                    label={val}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: 11,
                      height: 22,
                      bgcolor: '#e0e7ff',
                      color: '#3730a3',
                      border: '1px solid #c7d2fe'
                    }}
                  />
                );
              }
            }
          ]
        : []),
      {
        field: 'role',
        headerName: 'Role',
        width: 135,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.role || r.role_name || r.roleName || value || '-';
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.role || r.role_name || r.roleName || params.value || '-';
          if (val === '-') {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          return (
            <Chip
              label={val}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: 11.5,
                height: 24,
                borderColor: '#cbd5e1',
                color: '#334155',
                bgcolor: '#f8fafc'
              }}
            />
          );
        }
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Login At',
        width: 175,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.lastLoginAt || r.last_login_at || r.lastLogin || value || null;
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.lastLoginAt || r.last_login_at || r.lastLogin || params.value;
          return (
            <Typography variant="body2" sx={{ fontSize: 13, color: val ? 'text.primary' : 'text.disabled' }} noWrap>
              {val ? dayjs(val).format(layoutFormatDate) : '-'}
            </Typography>
          );
        }
      },
      {
        field: 'lastAccessEndpointAt',
        headerName: 'Last Access Endpoint At',
        width: 185,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.lastAccessEndpointAt || r.last_access_endpoint_at || r.lastAccess || value || null;
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.lastAccessEndpointAt || r.last_access_endpoint_at || r.lastAccess || params.value;
          return (
            <Typography variant="body2" sx={{ fontSize: 13, color: val ? 'text.primary' : 'text.disabled' }} noWrap>
              {val ? dayjs(val).format(layoutFormatDate) : '-'}
            </Typography>
          );
        }
      },
      {
        field: 'durationHms',
        headerName: 'Duration',
        width: 115,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.durationHms || r.duration_hms || r.duration || value || '-';
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.durationHms || r.duration_hms || r.duration || params.value || '-';
          return (
            <Typography variant="body2" sx={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 500 }} noWrap>
              {val}
            </Typography>
          );
        }
      },
      {
        field: 'loginCount',
        headerName: 'Login Count',
        width: 110,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          return r.loginCount ?? r.login_count ?? value ?? 0;
        },
        renderCell: (params) => {
          const r = params.row || {};
          const val = r.loginCount ?? r.login_count ?? params.value ?? 0;
          return (
            <Chip
              label={val}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                height: 22,
                minWidth: 32,
                bgcolor: '#f1f5f9',
                color: '#334155'
              }}
            />
          );
        }
      },
      {
        field: 'accessEndpointCount',
        headerName: 'Access Endpoint Count',
        width: 175,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => {
          const r = row || {};
          const endpoints = r.endpointHistory || r.endpoint_history || [];
          return r.accessEndpointCount ?? r.access_endpoint_count ?? (Array.isArray(endpoints) ? endpoints.length : 0) ?? value ?? 0;
        },
        renderCell: (params) => {
          const r = params.row || {};
          const endpoints = r.endpointHistory || r.endpoint_history || [];
          const count =
            r.accessEndpointCount ?? r.access_endpoint_count ?? (Array.isArray(endpoints) ? endpoints.length : 0) ?? params.value ?? 0;
          return (
            <Chip
              label={count}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: 11.5,
                height: 22,
                minWidth: 36,
                bgcolor: '#e0f2fe',
                color: '#0369a1',
                border: '1px solid #bae6fd'
              }}
            />
          );
        }
      },
      {
        field: 'actions',
        headerName: 'Action',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => openDetail(params.row)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              px: 1.5,
              py: 0.25,
              fontSize: 12,
              fontWeight: 600
            }}
          >
            Detail
          </Button>
        )
      }
    ],
    [activeTab, layoutFormatDate, openDetail]
  );

  return (
    <Box sx={{ backgroundColor: '#f5f7fb', minHeight: '100vh' }}>
      <Stack spacing={2}>
        {/* PAGE HEADER BANNER */}
        <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <CardContent sx={{ p: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                User Activity History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitoring and history of user endpoint activity
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* FILTERS */}
        <UserActivityFilters
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedCompanies={selectedCompanies}
          setSelectedCompanies={setSelectedCompanies}
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          loading={loading}
          fetchActivityHistory={fetchActivityHistory}
        />

        {/* WARNING ALERT */}
        {warningMessage && (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            {warningMessage}
          </Alert>
        )}

        {/* MAIN DATA TABLE */}
        <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={data}
                columns={columns}
                getRowId={(row, index) =>
                  `${row.userId || row.user_id || 'u'}-${row.ip || ''}-${row.lastLoginAt || row.last_login_at || index}`
                }
                loading={loading}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 25,
                      page: 0
                    }
                  }
                }}
                density="compact"
                rowHeight={52}
                columnHeaderHeight={52}
                headerHeight={52}
                disableRowSelectionOnClick
                slots={{
                  noRowsOverlay: () => (
                    <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                      {loading ? (
                        <CircularProgress size={32} />
                      ) : (
                        <>
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {selectedUsers.length === 0
                              ? 'Silakan pilih user dan klik Search untuk memuat data.'
                              : 'Tidak ada data user activity history untuk filter yang dipilih.'}
                          </Typography>
                        </>
                      )}
                    </Stack>
                  )
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f8fafc',
                    color: '#334155',
                    fontWeight: 700,
                    fontSize: 13,
                    borderBottom: '1px solid #e2e8f0'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700
                  },
                  '& .MuiDataGrid-cell': {
                    borderColor: '#f1f5f9',
                    fontSize: 13.5,
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#f8fafc'
                  }
                }}
              />
            </Box>
          </Box>
        </Card>
      </Stack>

      {/* DETAIL DIALOG */}
      <UserActivityDetailDialog
        open={Boolean(selectedDetailUser)}
        onClose={closeDetail}
        user={selectedDetailUser}
        searchDetail={searchDetail}
        setSearchDetail={setSearchDetail}
        filteredEndpointHistory={filteredEndpointHistory}
      />
    </Box>
  );
}
